import {Component} from '@angular/core';
import {BasicEventPlayer} from "../../entities/auth/BasicEventPlayer";
import {CosmeticsPlayer} from "../../entities/players/CosmeticsPlayer";
import {APIService} from "../../services/APIService";
import {MatDialog} from "@angular/material/dialog";
import {RankDialogueComponent} from "./rank-dialogue/rank-dialogue.component";
import {RoleDialogueComponent} from "./role-dialogue/role-dialogue.component";
import {MatSnackBar} from "@angular/material/snack-bar";
import {ActivatedRoute, Router} from "@angular/router";
import {Autograph} from "../../entities/players/Autograph";
import {AuthService} from "../../services/AuthService";
import {Punishment} from "../../entities/players/Punishment";
import {PunishDialogueComponent} from "./punish-dialogue/punish-dialogue.component";
import {RedisPlayer} from "../../entities/players/RedisPlayer";
import {EventPlayerYear} from "../../entities/players/EventPlayerYear";
import {QuashDialogueComponent} from "../punishments/quash-dialogue/quash-dialogue.component";
import {CoinsDialogueComponent} from "./coins-dialogue/coins-dialogue.component";

@Component({
  selector: 'app-player-view',
  templateUrl: './player-view.component.html',
  styleUrls: ['./player-view.component.css']
})
export class PlayerViewComponent {

  player: BasicEventPlayer | null = null;
  redisPlayer: RedisPlayer | null = null;
  eventPass: CosmeticsPlayer | null = null;
  // Latest coin balance fetched via node-api GET /players/:identifier/coins
  playerCoins: number | null = null;
  attendance: EventPlayerYear[] = [];

  columns: string[] = ['year', 'rank']

  autographs: Autograph[] = [];
  autographColumns: string[] = ['giver', 'receiver', 'text', 'actions'];

  punishColumns: string[] = ['player', 'mod', 'type', 'reason', 'status']
  punishments: Punishment[] = [];
  banned: boolean = false;
  muted: boolean = false;

  constructor(protected apiService: APIService,
              private dialog: MatDialog,
              private snack: MatSnackBar,
              private route: ActivatedRoute,
              private router: Router,
              protected authService: AuthService) {
    if ('id' in route.snapshot.params) {
      this.apiService.getPlayer(route.snapshot.params['id']).then((a) => this.select(a));
    }
  }

  openRank() {
    this.dialog.open(RankDialogueComponent, {data: this.player}).afterClosed().subscribe((data) => {
      const rank: number[] = data['rank']

      this.apiService.updatePermissions(this.player!, rank, []).then((a) => {
        this.apiService.getPlayer(this.player!.uuid).then((a) => this.player = a);
        this.snack.open("Success!");
      }).catch((e) => {
        this.snack.open("Error: " + e.message);
      })
    });
  }

  getTeamName() {
    if (this.eventPass === null)
      return;
    const original = this.eventPass.team!;
    return original.split("_")[1]
  }

  getRoleName(role: number) {
    return this.apiService.getRoleName(role)
  }

  openRole() {
    this.dialog.open(RoleDialogueComponent, {data: this.player}).afterClosed().subscribe(async (data) => {
      if (!data)
        return;
      const selected: number[] = data['roles'].map((x: string) => parseInt(x, 10)) || [];
      const current = this.player!.roles || [];
      const toAdd = selected.filter((r) => !current.includes(r));
      const toRemove = current.filter((r) => !selected.includes(r));
      try {
        // apply adds
        await Promise.all(toAdd.map((r) => this.apiService.addPlayerRole(this.player!, r)));
        // apply removals
        await Promise.all(toRemove.map((r) => this.apiService.removePlayerRole(this.player!, r)));
        this.apiService.getPlayer(this.player!.uuid).then((a) => this.player = a);
        this.snack.open("Success!");
      } catch (e: any) {
        this.snack.open("Error: " + (e?.message || e));
      }
    });
  }

  openPunish() {
    this.dialog.open(PunishDialogueComponent, {data: this.player}).afterClosed().subscribe((data) => {
      if (data === undefined)
        return;

      this.apiService.punish(this.player!, data.type, data.reason, data.expiry).then((r) => this.calculatePunishes());
    });
  }

  openCoins() {
    if (!this.player) return;
    const uuid = this.player!.uuid;
    this.dialog.open(CoinsDialogueComponent, { data: { player: this.player, coins: this.eventPass?.eventCoins } }).afterClosed().subscribe(async (data) => {
      try {
        if (!data) return;
        const amount = Number(data.amount);
        const reason = data.reason as (string | undefined);
        const mode = (data.mode || 'give') as 'give' | 'take';
        if (!Number.isFinite(amount) || amount <= 0) {
          this.snack.open('Please enter a positive amount');
          return;
        }
        if (mode === 'give') {
          await this.apiService.givePlayerCoins(uuid, amount, reason, false);
        } else {
          await this.apiService.takePlayerCoins(uuid, amount, reason, false);
        }
        // refresh event pass
        this.apiService.getEventPass(uuid).then((data) => this.eventPass = data);
        // refresh fetched coin balance
        this.refreshCoins();
        this.snack.open('Coins updated');
      } catch (e: any) {
        this.snack.open('Error: ' + (e?.message || e));
      }
    });
  }

  select(player: BasicEventPlayer) {
    this.router.navigateByUrl("/players/" + player.uuid)
    this.player = player;
    this.calculatePunishes()
    this.apiService.getEventPass(player.uuid).then((data) => this.eventPass = data);
    this.apiService.getGivenAutographs(player.uuid).then((data) => this.autographs = data);
    this.apiService.getStatus(player.uuid).then((data) => this.redisPlayer = data);
    this.apiService.getAttendance(player).then((data) => this.attendance = data);
    this.refreshCoins();
  }

  private async refreshCoins() {
    try {
      if (!this.player) return;
      const coins = await this.apiService.getPlayerCoins(this.player.uuid);
      this.playerCoins = coins;
    } catch (e) {
      // leave playerCoins as-is on error
    }
  }

  calculatePunishes() {
    this.apiService.getPunishmentsForPlayer(this.player!).then((data) => {
      this.punishments = data
      this.banned = false;
      this.muted = false;
      for (const punishment of this.punishments) {
        if (this.isActive(punishment)) {
          if (punishment.type === 'MUTE') {
            this.muted = true;
          }
          if (punishment.type === 'BAN') {
            this.banned = true;
          }
        }
      }
    });
  }

  isActive(punishment: Punishment): boolean {
    if (punishment.quashed) {
      return false;
    }

    if (punishment.ends === undefined && punishment.quashed) {
      return false;
    } else if (punishment.ends === undefined) {
      return true;
    } else {
      const ends = new Date(Number(punishment.ends!))
      return ends.getTime() > Date.now();
    }
  }

  formatNumber(number: number): string {
    return new Intl.NumberFormat("en-en").format(number);
  }

  quash(punishment: Punishment) {
    const dialogRef = this.dialog.open(QuashDialogueComponent, {
      data: punishment,
    });

    dialogRef.afterClosed().subscribe(result => {
      if(result) {
        this.apiService.quashPunishment(punishment.id)
          .then((a) => this.calculatePunishes());
      }
    });
  }

  getStatus(punishment: Punishment): string {
    if (punishment.quashed)
      return "Quashed";
    if (!punishment.active)
      return "Expired";
    if (punishment.ends === undefined)
      return "Permanent";
    const ends = Date.parse(punishment.ends!);
    if (ends > Date.now())
      return "Ends " + punishment.ends!;
    return "Expired";
  }

  protected readonly event = event;
  protected readonly open = open;
  protected readonly Event = Event;
}
